import { useEffect, useRef, useState } from "react";
import Section from "./Section";
import { translations, type Lang } from "../content";

/*
 * Stacked service list after the supplied reference: hairline-separated rows,
 * the active one grows, brightens and reveals its description alongside.
 *
 * Rows are presentational: no hover, click or focus handling.
 *
 * Sizing and reveal run on CSS transitions rather than JS animation, so the
 * whole interaction costs nothing at runtime.
 */
export default function Services({ lang }: { lang: Lang }) {
  const t = translations[lang];
  const rtl = lang === "he";
  const [active, setActive] = useState(0);
  const rowsRef = useRef<(HTMLDivElement | null)[]>([]);

  /*
   * Open whichever row is passing through the middle of the viewport, so the
   * list reveals itself on scroll. Scroll position is the only trigger - there
   * is no hover or click handling, so it behaves identically on touch.
   */
  useEffect(() => {
    const rows = rowsRef.current.filter(Boolean) as HTMLDivElement[];
    if (!rows.length || typeof IntersectionObserver === "undefined") return;

    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const i = rows.indexOf(entry.target as HTMLDivElement);
          if (i !== -1) setActive(i);
        }
      },
      /* Only a band across the middle of the screen counts as "current". */
      { rootMargin: "-40% 0px -40% 0px", threshold: 0 },
    );

    rows.forEach((row) => io.observe(row));
    return () => io.disconnect();
  }, [lang]);

  return (
    <Section id="services" compact>
      <div dir={rtl ? "rtl" : "ltr"}>
        <div className="border-t border-border/10">
          {t.services.items.map((item, i) => {
            const isActive = i === active;
            return (
              <div
                key={item.title}
                ref={(el) => {
                  rowsRef.current[i] = el;
                }}
                data-active={isActive}
                className="block w-full border-b border-border/10 py-6 md:py-8
                           ltr:text-left rtl:text-right"
              >
                <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-10">
                  {/* Title: grows and brightens when active. */}
                  <h3
                    className={`display-lg shrink-0 transition-all duration-500 ease-out
                                ${
                                  isActive
                                    ? "text-4xl md:text-6xl lg:text-7xl text-foreground"
                                    : "text-2xl md:text-4xl lg:text-5xl text-muted-foreground/50"
                                }`}
                  >
                    {item.title}
                    <span
                      className={`text-primary transition-opacity duration-500 ${
                        isActive ? "opacity-100" : "opacity-0"
                      }`}
                    >
                      .
                    </span>
                  </h3>

                  {/* Description: fades and slides in beside the title. */}
                  <p
                    className={`text-sm md:text-base text-muted-foreground font-light leading-relaxed
                                max-w-md transition-all duration-500 ease-out
                                ${
                                  isActive
                                    ? "opacity-100 translate-y-0 max-h-32"
                                    : "opacity-0 md:translate-y-2 max-h-0 md:max-h-32 overflow-hidden"
                                }`}
                  >
                    {item.text}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </Section>
  );
}
